import { defineStore } from 'pinia';
import {
	Bbay,
	Offergroup,
	Request,
	UserTypes,
	RequestProcessModel,
	Sort,
	RequestCustomTypeModel,
	Species,
} from '@fibl/oxs-common';
import { useWebsiteStore } from './website';
import { useOfferSearchStore } from './offersearch';
import { useBbayStore } from './bbay';
import * as RequestRepo from '@/repository/request';
import { useTokenStore } from './token';
import type { EAV, Search } from '@fibl/zdb-common';
import { useOfferGroupStore } from './offergroup';
import { useRelatedStore } from './related';
import { useSelectOptionStore } from './selectoption';
import { getI18n } from '@/util/i18n';

export const useRequestInitStore = defineStore('requestInit', {
	state: () => ({
		requestType: '',
		selectedAgr: '',
		activeRegion: '',
		classificationResults: { request: {} } as Bbay.ClassificationCheck,
		ornamentalSpecies: null as null|EAV.Entity<Species.Species>,
	}),

	persist: {
		paths: ['selectedAgr', 'activeRegion', 'classificationResults'],
	},

	getters: {
		bbayEnv(): Bbay.ClassificationCheck {
			const searchStore = useOfferSearchStore();
			// console.log('new bbay env', searchStore.cf.menu?.data.categoryFilter)
			let catsBase = searchStore.cf.menu?.data.categoryFilter || [];
			const country = useWebsiteStore().countryCode;
			if (searchStore.cf.activeSpecies) {
				const speciesCats = searchStore.cf.activeSpecies?.data.countrySpecific[country]?.categories || [];
				catsBase = catsBase.filter(id => speciesCats.includes(id));
			}
			return {
				env: {
					country,
					species: searchStore.cf.activeSpecies?.id || '',
					category: catsBase,
					materialType: searchStore.effectiveMaterialFilter || '',
					ins: this.insId,
					region: this.activeRegion,
					requestType: this.requestType,
				},
				request: {},
			};
		},
		requestInternalType(): Request.RequestType {
			if (!this.requestType) return 'default';
			let e = useSelectOptionStore().byId(this.requestType);
			if (!e) return 'default';
			if (e instanceof RequestCustomTypeModel) return e.getRequestType();
			return 'default';
		},
		getRequestPackageForClassification() {
			return (classificationId: string): string | null => {
				return this.classificationResults.request[classificationId]?.id || null;
			};
		},
		canShowFilters(): boolean {
			const userStore = useTokenStore();
			let role = userStore.role;
			return !!(this.bbayEnv.env.region && (role === 'agr' || this.selectedAgr));
		},
		canRunBbay(): boolean {
			return !!(this.bbayEnv.env.species && this.bbayEnv.env.category.length && this.canShowFilters);
		},
		offergroupsAvailableForRequest(): EAV.Entity<Offergroup.Offergroup>[] {
			const cc = this.bbayEnv.env.country;
			const matFilter = this.bbayEnv.env.materialType;
			if (!matFilter) return [];
			const all = useOfferGroupStore().loaded.filter(
				(og) => og.data.species === this.bbayEnv.env.species
					&& og.data.country === cc
					&& (!og.data.matType?.length || og.data.matType?.includes(matFilter))
					&& og.data.active
			);
			const searchStore = useOfferSearchStore();
			const species = searchStore.cf.activeSpecies;
			if (!species?.data.countrySpecific[cc]?.offergroupsDictatedBySort) {
				return all;
			} else {
				const sortId = searchStore.cf.baseFilters.sort;
				if (!sortId) return all;
				const sort = useRelatedStore().byId(sortId) as EAV.Entity<Sort.Sort>;
				const sortgroups = sort.data.countrySpecific[cc]?.groups || [];
				if (!sortgroups.length) return all;
				return all.filter((og) => sortgroups.includes(og.id));
			}
		},
		role() {
			return useTokenStore().role;
		},
		userDetail() {
			return useTokenStore().main.details;
		},
		insId(): string {
			if (this.role === 'ins') return this.userDetail.id;
			const agr = (
				this.role === 'agr'
					? this.userDetail
					: useRelatedStore().byId(this.selectedAgr)?.data
			) as UserTypes.AccountAgr;
			if (!agr) return '';
			return agr?.staticData?.controlbody || '';
		},
		insFilter(): Search.SearchFilter {
			if (this.role === 'agr') {
				const data = this.userDetail.staticData as UserTypes.StaticDataAgr;
				return {
					$and: [
						{
							field: '/id',
							comparison: 'eq',
							value: data.controlbody,
						},
						{
							field: '/data/staticData/country',
							comparison: 'eq',
							value: useWebsiteStore().countryCode,
						},
					],
				};
			}
			return {
				$and: [],
			};
		},
		conFilter(): Search.SearchFilter {
			if (this.role === 'agr') {
				const data = this.userDetail.userData as UserTypes.UserDataAgr;
				const conFilter: Search.SearchFilter = {
					$and: [
						{
							field: '/data/staticData/country',
							comparison: 'eq',
							value: useWebsiteStore().countryCode,
						},
					],
				};
				const accFilter: Search.SearchFilter = { $or: [] };
				for (let acc of data.accessForConsultants) {
					accFilter.$or.push({
						field: '/id',
						comparison: 'eq',
						value: acc.account,
					});
				}
				conFilter.$and.push(accFilter);
				return conFilter;
			}
			return {
				$and: [],
			};
		},
		agrFilter(): Search.SearchFilter {
			return {
				$and: [
					this.agrFilterRole,
					{
						field: '/data/staticData/country',
						comparison: 'eq',
						value: useWebsiteStore().countryCode,
					},
				],
			};
		},
		agrFilterRole(): Search.SearchFilter {
			switch (this.role) {
				default: {
					return { $and: [] };
				}
				case 'con': {
					return {
						field: '/data/userData/accessForConsultants/account',
						comparison: 'eq',
						value: this.userDetail.id,
					};
				}
				case 'ins': {
					return {
						field: '/data/staticData/controlbody',
						comparison: 'eq',
						value: this.userDetail.id,
					};
				}
				case 'aut': {
					const staticSelf = this.userDetail.staticData as UserTypes.StaticDataAut;
					return {
						$or: staticSelf.responsibleForRegions.map((region) => ({
							field: '/data/staticData/region',
							comparison: 'eq',
							value: region,
						})),
					};
				}
			}
		},
		isSortRequired(): boolean {
			return this.requestInternalType !== 'ornamental';
		},
	},

	actions: {
		async updateClassifications() {
			if (!this.canRunBbay) return;
			let res = await useBbayStore().runClassifications(this.bbayEnv);
			this.classificationResults = res;
		},

		async startRequest(offergroup: EAV.Entity<Offergroup.Offergroup>, sortText?: string) {
			const i18n = getI18n();
			let classificationId = offergroup.data.classification;
			const searchStore = useOfferSearchStore();
			let pkg = this.getRequestPackageForClassification(classificationId);
			if (!pkg) {
				throw new Error(i18n.t('stores.noReqPkgError'));
			}
			const userStore = useTokenStore();
			let role = userStore.role;
			let agr: string | undefined;
			switch (role) {
				case 'agr': {
					break;
				}
				case 'ins': {
					agr = this.selectedAgr;
					break;
				}
				case 'aut': {
					agr = this.selectedAgr;
					break;
				}
				case 'con': {
					agr = this.selectedAgr;
					break;
				}
				default: {
					throw new Error(i18n.t('stores.userRequestError'));
				}
			}
			const flags: string[] = [];
			if (searchStore.effectiveMaterialFilter === '-')
				throw new Error(i18n.t('stores.invalidMatError'));
			const params: RequestRepo.InitParams = {
				type: this.requestType,
				agr,
				offergroup: offergroup.id,
				pkg,
				searchFilter: {
					typeOfMaterial: searchStore.effectiveMaterialFilter || '-',
					prmStatus: searchStore.cf.baseFilters.prmStatus || undefined,
					category: this.bbayEnv.env.category,
				},
				flags,
			};
			if (this.isSortRequired) {
				if (searchStore.cf.baseFilters.sort)
					params.sortId = searchStore.cf.baseFilters.sort;
				else if ((sortText || '').length > 2) params.sortText = sortText;
				else throw new Error(i18n.t('stores.noSortError'));
			}
			let id = await RequestRepo.initRequest(params);
			return id;
		},

		async startRequestClone(process: RequestProcessModel, newAgr?: string) {
			const agr = newAgr || process.getMembersWithRole().find((m) => m.role === 'agr')?.id;
			if (!this.requestType) {
				this.requestType = process.extType;
			}
			const params: RequestRepo.InitParams = {
				type: this.requestType,
				agr,
				offergroup: process.offergroup.id,
				pkg: process.package.id,
				searchFilter: process.searchFilter,
				sortId: process.sort && 'id' in process.sort ? process.sort?.id : undefined,
				sortText: process.sort && 'text' in process.sort ? process.sort?.text : undefined,
			};
			let id = await RequestRepo.initRequest(params);
			return id;
		},

		async fillRequest(
			request: RequestProcessModel,
			fieldData: Record<string, any>,
			attachments: string[],
			fixedData?: Request.RequestFixedData,
		) {
			let res = await RequestRepo.fillForm({
				process: request.getId(),
				attachments: attachments.map((id) => ({ id })),
				fieldData,
				fixedData,
			});
			request.fromEntity(res);
		},

		async checkIfProcessIsCloneable(process: RequestProcessModel) {
			const i18n = getI18n();
			const agrId = process.getMembersWithRole().find((m) => m.role === 'agr')?.id;
			if (!agrId) throw new Error(i18n.t('stores.noAgrError'));
			const agr = (await useRelatedStore().addEntity(
				agrId,
			)) as EAV.Entity<UserTypes.AccountAgr>;
			const bbayEnv: Bbay.ClassificationCheck = {
				env: {
					country: process.country,
					species: process.species.id,
					category: process.searchFilter.category || '',
					ins: process.members.find((m) => m.role === 'ins')?.id || '',
					materialType: process.searchFilter.typeOfMaterial || '',
					region: agr.data.staticData.region,
					requestType: process.extType,
				},
				request: {},
			};
			let res = await useBbayStore().runClassifications(bbayEnv);
			const pkgId = res.request[process.classification.id]?.id;
			if (!pkgId || pkgId !== process.package.id) {
				console.debug('pkg id does not match', {
					expected: process.package.id,
					got: pkgId,
				});
				return false;
			}
			let pkg = (await useRelatedStore().addEntity(
				pkgId,
			)) as EAV.Entity<Request.RequestPackage>;
			if (pkg.rev !== process.package.rev) {
				console.debug('pkg rev does not match', {
					expected: process.package.rev,
					got: pkg.rev,
				});
				return false;
			}
			return true;
		},


	},
});
